/** Foreman-specific message types for structured output parsing */

export interface ForemanMeta {
  confidence: string;
  skill: string;
  source: string;
}

export interface ForemanItem {
  type: 'ok' | 'warn' | 'info';
  text: string;
}

export interface ForemanPricingLine {
  cat: string;
  amt: string;
  note: string;
}

export interface ForemanPricing {
  lines: ForemanPricingLine[];
  total: string | null;
}

export interface ForemanAlert {
  type: string;
  title: string;
  text: string;
}

export interface ForemanProgress {
  label: string;
  done: boolean;
}

export interface ForemanChoice {
  title: string;
  description?: string;
  options: string[];
}

export interface ForemanCorrection {
  field: string;
  original: string;
  corrected: string;
  context: string;
  corrected_by: string;
}

export interface ForemanPractice {
  title: string;
  category: string;
  priority: string;
  taught_by: string;
}

export interface ForemanLearningReport {
  period: string;
  corrections: string;
  practices: string;
  trend: string;
  accuracy: string;
}

export interface ForemanParsedResponse {
  text: string;
  meta: ForemanMeta | null;
  items: ForemanItem[] | null;
  pricing: ForemanPricing | null;
  alert: ForemanAlert | null;
  progress: ForemanProgress[] | null;
  actions: string[] | null;
  choices: ForemanChoice[] | null;
  correction: ForemanCorrection | null;
  practice: ForemanPractice | null;
  learningReport: ForemanLearningReport | null;
}

export type MessageType = 'user' | 'foreman' | 'system';

export type JobStatus = 'active' | 'ready' | 'attention' | 'complete';

export interface JobThread {
  id: string;
  name: string;
  code: string;
  status: JobStatus;
  unread: number;
  lastMessage: string;
  lastMessageAt: string;
  role: 'designer' | 'field' | 'accounting';
}

export interface ChatMessage {
  id: string;
  type: MessageType;
  content: string;
  parsed?: ForemanParsedResponse;
  sender: string;
  timestamp: string;
  sessionId: string;
}

export interface KnowledgeEntry {
  id: number;
  type: 'correction' | 'practice' | 'product_update';
  title: string;
  description: string;
  source: string;
  timestamp: string;
  appliedTo: string[];
  confidence?: number;
}

export interface ConfidenceStats {
  overall: number;
  bySkill: Record<string, { confidence: number; corrections: number; trend: 'improving' | 'stable' | 'declining' }>;
  byCategory: Record<string, number>;
  recentCorrections: number;
  knowledgeEntries: number;
  patternDbJobs: number;
  lowConfidenceAreas: string[];
}

export type QuickAction = {
  label: string;
  icon: string;
  prompt: string;
};

export const QUICK_ACTIONS: Record<string, QuickAction[]> = {
  designer: [
    { label: 'Submit for pricing', icon: '\u{1F4B0}', prompt: 'Price this scope for me' },
    { label: 'Product question', icon: '\u{2753}', prompt: 'I have a product question: ' },
    { label: 'Check job status', icon: '\u{1F4CB}', prompt: 'What is the status of job ' },
    { label: 'Waste calculator', icon: '\u{1F4D0}', prompt: 'Calculate waste factor for ' },
  ],
  field: [
    { label: 'Pull install pack', icon: '\u{1F4E6}', prompt: 'Generate install pack for job ' },
    { label: 'Cart status', icon: '\u{1F6D2}', prompt: 'What is the cart status for job ' },
    { label: 'Material question', icon: '\u{1F9F1}', prompt: 'Material question: ' },
    { label: 'Report issue', icon: '\u{26A0}\u{FE0F}', prompt: 'Reporting a field issue: ' },
  ],
  accounting: [
    { label: 'Run AMX audit', icon: '\u{1F4B3}', prompt: 'Run the AMX receipt audit for this week' },
    { label: 'Job cost summary', icon: '\u{1F4CA}', prompt: 'Show job cost summary for ' },
    { label: 'CO report', icon: '\u{1F4DD}', prompt: 'Generate change order report for ' },
    { label: 'WIP verification', icon: '\u{2705}', prompt: 'Run WIP verification for all active jobs' },
  ],
};
